/**
 * G12: the PWA app badge (`navigator.setAppBadge`) shows the needs-you count, the same number as
 * the Inbox badge. The page is authoritative (it knows what is resolved); while the app is closed
 * the service worker adds the ids of pushed asks to the same local set, so the badge rises with
 * each push and the next page load reconciles it.
 */
import { idb } from './idb.js'

const KEY = 'badge/ids'
const MAX_IDS = 999

interface BadgeNav {
  setAppBadge?(n?: number): Promise<void>
  clearAppBadge?(): Promise<void>
}

export async function applyBadge(
  count: number,
  nav: BadgeNav | undefined = (globalThis as { navigator?: BadgeNav }).navigator,
): Promise<boolean> {
  if (!nav?.setAppBadge) return false
  try {
    if (count > 0) await nav.setAppBadge(count)
    else if (nav.clearAppBadge) await nav.clearAppBadge()
    else await nav.setAppBadge(0)
    return true
  } catch {
    return false
  }
}

/** Service worker: a blocking ask was pushed. Returns the new count. */
export async function addBlocked(ids: readonly string[]): Promise<number> {
  const next = await idb.update<string[]>(KEY, (cur) =>
    [...new Set([...(cur ?? []), ...ids])].slice(-MAX_IDS),
  )
  return next.length
}

/** Page: the authoritative set of needs-you ids. Returns the count applied. */
export async function replaceBlocked(ids: readonly string[]): Promise<number> {
  const unique = [...new Set(ids)].slice(-MAX_IDS)
  await idb.set(KEY, unique).catch(() => {})
  await applyBadge(unique.length)
  return unique.length
}
